import { useMemo, useState, useEffect, useRef } from 'react';
import { MessageSquare, MessageCircle, Inbox, Calendar, Settings, Users, Phone, PhoneOff, Loader2, PhoneCall, GitBranch, ArrowRightLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useUIStore } from '@/stores/ui-store';
import { useAuthStore } from '@/stores/auth-store';
import { useAppStore } from '@/stores/app-store';
import { useCallStore, selectHasDismissedActiveCalls } from '@/stores/call-store';
import { isLiveCallStatus } from '@/types/voice';
import { useModuleStore } from '@/stores/module-store';
import { useSettingsStore } from '@/stores/settings-store';
import { CreditBadge } from '@/components/common/credit-badge';
import { NotificationBell } from '@/components/common/notification-bell';
import { TrialBadge } from '@/components/trial/trial-badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '@/components/ui/dialog';
import { SharedAdminChooser } from '@/components/auth/shared-admin-chooser';
import { EVENT_APPS, CALL_APPS } from '@/config/constants';
import type { ViewType } from '@/config/constants';
import styles from './header.module.css';
import { INBOX_SUPPORTED_TPAS, NON_MESSAGING_TPAS, CHAT_ONLY_TPAS } from '@/config/constants';
import { useContainerSize } from '@/hooks/use-container-size';

type NavItem = { view: ViewType; label: string; icon: typeof MessageSquare };

const DEFAULT_NAV_ITEMS: NavItem[] = [
  { view: 'MESSAGE_FORM', label: 'Compose', icon: MessageSquare },
  { view: 'CONVERSATIONS', label: 'Inbox', icon: Inbox },
];

const CRM_NAV_ITEMS: NavItem[] = [
  { view: 'MESSAGE_FORM', label: 'Compose', icon: MessageSquare },
  { view: 'CHAT', label: 'Chat', icon: MessageCircle },
  { view: 'CONVERSATIONS', label: 'Inbox', icon: Inbox },
];

const EVENT_NAV_ITEMS: NavItem[] = [
  { view: 'EVENTS', label: 'Events', icon: Calendar },
];

const CALL_NAV_ITEMS: NavItem[] = [
  { view: 'CONVERSATIONS', label: 'Call History', icon: PhoneCall },
  { view: 'PHONE_TREE', label: 'Phone Tree', icon: GitBranch },
];

/** Placeholder skeleton tabs shown while app-init resolves CRM context */
function NavSkeleton({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className={styles.skeleton}
          style={{ width: `${60 + i * 10}px` }}
        />
      ))}
    </>
  );
}

export function Header({ hideNav, hideNotification }: { hideNav?: boolean; hideNotification?: boolean } = {}) {
  const activeView = useUIStore((s) => s.activeView);
  const setView = useUIStore((s) => s.setView);
  const viewResolved = useUIStore((s) => s.viewResolved);
  const crmRecordContext = useUIStore((s) => s.crmRecordContext);
  const crmPhoneFields = useUIStore((s) => s.crmPhoneFields);
  const user = useAuthStore((s) => s.user);
  const appCodeTPA = useAppStore((s) => s.appCodeTPA) ?? '';
  const selectedAdminUserId = useAuthStore((s) => s.selectedAdminUserId);
  const invitedAdminList = useAuthStore((s) => s.invitedAdminList);
  const fetchInvitedAdminList = useAuthStore((s) => s.fetchInvitedAdminList);
  const [orgChooserOpen, setOrgChooserOpen] = useState(false);

  // Lazy-load the invited admin list so we know whether to surface the
  // switch-organization affordance on the profile pill. Cheap (one
  // Firestore read, in-memory cached after first call).
  useEffect(() => {
    if (invitedAdminList === null) {
      fetchInvitedAdminList().catch(() => {});
    }
  }, [invitedAdminList, fetchInvitedAdminList]);

  // Show the switch-org pill whenever the user has at least one
  // organization invite to fall back on. Two scenarios:
  //   - User on own account + has invites → can switch INTO a shared org.
  //   - User on shared org + has another invite → can switch BETWEEN orgs.
  // The list excludes the currently-selected admin so this is always
  // about *other* options.
  const otherOrgsAvailable = useMemo(() => {
    if (!invitedAdminList) return false;
    return Object.values(invitedAdminList).some(
      (a) => a.invitedAdminUserID !== selectedAdminUserId,
    );
  }, [invitedAdminList, selectedAdminUserId]);
  const appCodeSAAS = useAppStore((s) => s.appCodeSAAS) ?? '';
  const inboxSupported = INBOX_SUPPORTED_TPAS.has(appCodeTPA);
  const isGoogleSheets = appCodeSAAS === 'googlesheets';
  const isNonMessaging = NON_MESSAGING_TPAS.has(appCodeTPA);
  const { isCompact, isMini } = useContainerSize();

  const isCallApp = appCodeTPA && (CALL_APPS as readonly string[]).includes(appCodeTPA);

  const navItems = useMemo(() => {
    // 1. Event apps — no nav tabs (events view has its own internal tabs)
    if (appCodeTPA && (EVENT_APPS as readonly string[]).includes(appCodeTPA)) {
      return [];
    }
    // 2. Call-centric apps — Phone Tree only for admins
    if (isCallApp) {
      const isAdmin = !selectedAdminUserId;
      return isAdmin ? CALL_NAV_ITEMS : CALL_NAV_ITEMS.filter((i) => i.view !== 'PHONE_TREE');
    }
    // 3. Non-messaging apps
    if (isNonMessaging) {
      return [];
    }
    // 4. Base nav items
    const baseItems = crmRecordContext ? CRM_NAV_ITEMS : DEFAULT_NAV_ITEMS;
    // 5. Filter inbox if not supported
    return baseItems.filter((item) => {
      if (!inboxSupported && (item.view === 'CONVERSATIONS' || item.view === 'CHAT')) {
        return false;
      }
      // Google Sheets doesn't have CRM record-level chat or inbox — hide Chat & Inbox views
      if (isGoogleSheets && (item.view === 'CHAT' || item.view === 'CONVERSATIONS')) {
        return false;
      }
      // Hide all messaging nav items when Compose view is active in CRM multi-record context
      if (activeView === 'MESSAGE_FORM' && crmRecordContext) return false;
      // Hide Compose nav in single CRM record context (phone fields resolved = single record)
      if (crmRecordContext && crmPhoneFields.length > 0 && item.view === 'MESSAGE_FORM') return false;
      // Hide Inbox nav for chat-only TPAs (no conversation list API)
      if (appCodeTPA && CHAT_ONLY_TPAS.has(appCodeTPA) && item.view === 'CONVERSATIONS') return false;
      // Hide Inbox nav in mini view — not enough space
      if (isMini && item.view === 'CONVERSATIONS') return false;
      return true;
    });
  }, [appCodeTPA, crmRecordContext, crmPhoneFields.length, inboxSupported, isCallApp, isGoogleSheets, isNonMessaging, selectedAdminUserId, activeView, isMini]);

  return (
    <header className={styles.header}>
      <div className={styles['header-content']}>
        {/* Left: Logo + Nav */}
        <div className={styles['header-left']}>
          <h1 className={styles.logo}>
            {isMini ? (
              <img src={appCodeTPA === 'pinnacle' ? '/images/logos/pinnacle.png' : '/images/ulgebra-icon.png'} alt={appCodeTPA === 'pinnacle' ? 'Pinnacle' : 'Ulgebra'} className="h-6 w-6 rounded object-cover" />
            ) : (
              <>
                {appCodeTPA === 'pinnacle' ? 'Pinnacle' : 'Ulgebra'}
                {!isCompact && (
                  <span className="absolute -top-1.5 -right-9">
                    <TrialBadge />
                  </span>
                )}
              </>
            )}
          </h1>
          {!isCompact && <ExpandModalButton />}

          {!hideNav && navItems.length > 1 && (
            <nav className={isCompact ? styles['compact-nav'] : styles['desktop-nav']}>
              {!viewResolved ? (
                <NavSkeleton count={2} />
              ) : (
                navItems.map(({ view, label, icon: Icon }) => {
                  const isActive = activeView === view;
                  return (
                    <button
                      key={view}
                      data-testid={`nav-${label.toLowerCase()}`}
                      onClick={() => {
                        if (useCallStore.getState().crmCallManagerOpen) {
                          useCallStore.getState().toggleCrmCallManager();
                        }
                        setView(view);
                      }}
                      className={`${styles['nav-item']} ${isActive ? styles.active : ''}${isCompact ? ` ${styles['nav-item-compact']}` : ''}`}
                      title={label}
                    >
                      <Icon className={styles['nav-icon']} style={{ width: '16px', height: '16px' }} />
                      <span className={styles['nav-label']}>{label}</span>
                    </button>
                  );
                })
              )}
            </nav>
          )}
        </div>

        {/* Right: Actions */}
        <div className={styles['header-right']}>
          <HeaderPhoneButton />
          {/* Bell items open the inbox, which chat-only apps hide — so the
              bell goes with it. Desktop notifications are separate and stay. */}
          {!hideNotification && !isMini && !CHAT_ONLY_TPAS.has(appCodeTPA) && <NotificationBell />}

          {/* Settings */}
          <button
            data-testid="nav-settings"
            onClick={() => setView('SETTINGS')}
            className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            title="Settings"
          >
            <Settings className="h-4 w-4" />
          </button>

          {/* Profile — always visible, remove border separator in compact */}
          <div className={cn('relative', isCompact ? styles['profile-section-compact'] : styles['profile-section'])}>
            <button
              onClick={() => setView('SETTINGS')}
              className={styles['profile-button']}
              title="Settings"
            >
              {user?.photoURL ? (
                <img
                  src={user.photoURL}
                  alt={user.displayName}
                  className={styles['profile-image']}
                />
              ) : (
                <div className={styles['profile-initial']}>
                  {user?.displayName?.charAt(0)?.toUpperCase() || 'U'}
                </div>
              )}
              {selectedAdminUserId && !otherOrgsAvailable && (
                <span className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-amber-500 ring-2 ring-card">
                  <Users className="h-2 w-2 text-white" />
                </span>
              )}
            </button>

            {/* Switch-organization affordance. Only rendered when the user
                has at least one OTHER organization to switch into. Sits
                on top of the profile button (absolute-positioned) so a
                click hits this button instead of opening Settings.
                Replaces the static Users badge above when actionable. */}
            {otherOrgsAvailable && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOrgChooserOpen(true);
                }}
                title={selectedAdminUserId ? 'Switch organization' : 'Use a shared organization'}
                aria-label="Switch organization"
                className={cn(
                  'absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full ring-2 ring-card transition-colors',
                  selectedAdminUserId
                    ? 'bg-amber-500 hover:bg-amber-600'
                    : 'bg-primary hover:bg-primary/90',
                )}
              >
                <ArrowRightLeft className="h-2.5 w-2.5 text-white" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Organization chooser dialog — surfaces the same SharedAdminChooser
          used in the TPA-auth fallback flow so the switch UX is
          consistent everywhere. Triggered from the profile pill. */}
      <Dialog open={orgChooserOpen} onOpenChange={setOrgChooserOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Switch organization</DialogTitle>
            <DialogClose onClose={() => setOrgChooserOpen(false)} />
          </DialogHeader>
          <SharedAdminChooser onClose={() => setOrgChooserOpen(false)} />
        </DialogContent>
      </Dialog>

      {/* Mobile nav removed — nav items always stay in the header bar (icon-only at narrow widths) */}
    </header>
  );
}

/**
 * Expand button for ZFAPPS-based platforms (Zoho Books, etc.).
 * Opens the current app in a larger modal via ZFAPPS.showModal().
 * Only visible when embedded inside a Zoho Finance App (ZFAPPS SDK available).
 * Ported from old app's showExpandedModalView.
 */
function ExpandModalButton() {
  const zfapps = (window as any).ZFAPPS;
  if (!zfapps?.showModal) return null;

  // Don't show if already in modal mode
  const params = new URLSearchParams(window.location.search);
  if (params.get('openMode') === 'modal') return null;

  const handleExpand = () => {
    // Get module + entityId from the SaaS module's widget context
    // (resolved from SDK, not URL params — URL may not have them in SDK mode)
    const saasModule = useModuleStore.getState().saasModule;
    const wc = saasModule?.getWidgetContext?.();
    const module = wc?.module || '';
    const entityId = wc?.entityId || '';

    // Build URL exactly like old app:
    // window.location.href + "&openMode=modal&module=...&entityId=..."
    let url = window.location.href;
    url += (url.includes('?') ? '&' : '?') + 'openMode=modal';
    if (module) url += '&module=' + encodeURIComponent(module);
    if (entityId) url += '&entityId=' + encodeURIComponent(entityId);
    zfapps.showModal({ url });
  };

  return (
    <button
      onClick={handleExpand}
      className="ml-1 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
      title="Open in expanded view"
    >
      {/* Material Icons aspect_ratio */}
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19 12h-2v3h-3v2h5v-5zM7 9h3V7H5v5h2V9zm14-6H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16.01H3V4.99h18v14.02z"/>
      </svg>
    </button>
  );
}

/** Phone button with status dot for the top nav bar — opens Call Manager */
function HeaderPhoneButton() {
  const [showEnablePrompt, setShowEnablePrompt] = useState(false);
  const promptRef = useRef<HTMLDivElement>(null);

  const { isMini } = useContainerSize();
  const extensionName = useAppStore((s) => s.extensionName);
  const appCodeTPA = useAppStore((s) => s.appCodeTPA);
  const enableVoiceCalling = useSettingsStore((s) => s.enableVoiceCalling);
  const updateEnableVoiceCalling = useSettingsStore((s) => s.updateEnableVoiceCalling);
  const selectedAdminUserId = useAuthStore((s) => s.selectedAdminUserId);
  const softphoneState = useCallStore((s) => s.softphoneState);
  const voiceInitWaiting = useCallStore((s) => s.voiceInitWaiting);
  const isAvailable = useCallStore((s) => s.isAvailable);
  const toggleCallManager = useCallStore((s) => s.toggleCallManager);
  const toggleCrmCallManager = useCallStore((s) => s.toggleCrmCallManager);
  const crmCallManagerOpen = useCallStore((s) => s.crmCallManagerOpen);
  const callManagerOpen = useCallStore((s) => s.callManagerOpen);
  const activeCalls = useCallStore((s) => s.activeCalls);
  const hasDismissedActiveCalls = useCallStore(selectHasDismissedActiveCalls);
  const undismissAllCalls = useCallStore((s) => s.undismissAllCalls);

  const isTwilioExt = extensionName?.startsWith('twilio') ?? false;
  const isCallApp = appCodeTPA && (CALL_APPS as readonly string[]).includes(appCodeTPA);

  // Primary user (no shared admin) = can enable voice calling
  const isOrgAdmin = !selectedAdminUserId;

  // Close prompt on outside click
  useEffect(() => {
    if (!showEnablePrompt) return;
    const handler = (e: MouseEvent) => {
      if (promptRef.current && !promptRef.current.contains(e.target as Node)) {
        setShowEnablePrompt(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showEnablePrompt]);

  if (!isTwilioExt) return null;

  // For call apps (twiliocall), dialer is always visible in the side panel — don't show navbar button
  if (isCallApp) return null;

  const hasActiveCalls = activeCalls.some((c) => isLiveCallStatus(c.status));

  // Hide dialer button when floating call widget is visible (but keep it if widget is dismissed)
  if (hasActiveCalls && !hasDismissedActiveCalls) return null;

  const handleClick = () => {
    if (!enableVoiceCalling) {
      setShowEnablePrompt((prev) => !prev);
      return;
    }
    if (hasDismissedActiveCalls) {
      undismissAllCalls();
      return;
    }
    if (isMini) {
      toggleCrmCallManager();
    } else {
      toggleCallManager();
    }
  };

  const handleEnable = () => {
    updateEnableVoiceCalling(true);
    setShowEnablePrompt(false);
  };

  return (
    <div className="relative" ref={promptRef}>
      <button
        onClick={handleClick}
        data-call-manager-trigger
        className={cn(
          'relative rounded-lg p-2 transition-colors',
          !enableVoiceCalling
            ? 'text-muted-foreground/50 hover:bg-muted hover:text-muted-foreground'
            : (callManagerOpen || crmCallManagerOpen)
              ? 'bg-primary/10 text-primary'
              : hasActiveCalls
                ? 'text-emerald-600 hover:bg-emerald-500/10'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
        )}
        title={!enableVoiceCalling ? 'Voice Calling (Off)' : hasDismissedActiveCalls ? 'Reopen active call' : voiceInitWaiting ? 'Initializing voice...' : 'Call Manager'}
      >
        {!enableVoiceCalling ? (
          <PhoneOff className="h-4 w-4" />
        ) : voiceInitWaiting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Phone className="h-4 w-4" />
        )}
        {/* Active call indicator — pulsing green when call widget is dismissed */}
        {hasDismissedActiveCalls ? (
          <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-emerald-400 animate-pulse ring-2 ring-card" />
        ) : enableVoiceCalling && !voiceInitWaiting && (
          <span
            className={cn(
              'absolute top-1 right-1 h-2 w-2 rounded-full ring-2 ring-card',
              softphoneState === 'registered' && isAvailable
                ? 'bg-emerald-400'
                : softphoneState === 'registered' && !isAvailable
                  ? 'bg-amber-400'
                  : softphoneState === 'registering'
                    ? 'bg-blue-400 animate-pulse'
                    : softphoneState === 'unregistered'
                      ? 'bg-slate-400'
                      : 'bg-destructive'
            )}
          />
        )}
      </button>

      {/* Enable voice calling prompt */}
      {showEnablePrompt && (
        <div className="absolute top-full right-0 mt-2 z-50 w-64 rounded-xl border border-border bg-popover p-4 shadow-xl animate-in fade-in-0 zoom-in-95">
          <div className="flex items-center gap-2.5 mb-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <PhoneCall className="h-4 w-4 text-primary" />
            </div>
            <span className="text-sm font-semibold text-foreground">Voice Calling</span>
          </div>
          <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
            {isOrgAdmin
              ? 'Voice calling lets you make and receive calls directly from the app.'
              : 'Voice calling is not enabled. Ask your account admin to turn it on.'}
          </p>
          {isOrgAdmin && (
            <Button size="sm" onClick={handleEnable} className="w-full h-8 text-xs gap-1.5">
              <Phone className="h-3.5 w-3.5" />
              Enable Voice Calling
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
